import React, { useRef, useState, useLayoutEffect, useCallback, useEffect } from 'react';
import MonacoEditor from '@monaco-editor/react';
import { FaList, FaSave } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import './StudyModeCodeEditor.css';

const DARK_THEME_NAME = 'flashcode-study-dark';
const LIGHT_THEME_NAME = 'flashcode-study-light';

const DARK_THEME = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: '', foreground: '#ffffff', background: '#1a1a1f' },
    { token: 'comment', foreground: '#6b7280' },
    { token: 'keyword', foreground: '#3b82f6' },
    { token: 'string', foreground: '#10b981' },
    { token: 'number', foreground: '#f59e0b' },
    { token: 'function', foreground: '#8b5cf6' },
    { token: 'variable', foreground: '#ffffff' },
    { token: 'type', foreground: '#06b6d4' },
  ],
  colors: {
    'editor.background': '#0f0f13',
    'editor.foreground': '#ffffff',
    'editorLineNumber.foreground': '#6b7280',
    'editor.selectionBackground': '#3b82f620',
    'editorCursor.foreground': '#ffffff',
    'editor.lineHighlightBackground': '#1a1a1f',
    'editorGutter.background': '#0f0f13',
    'editor.lineHighlightBorder': '#3b82f610',
    'editorWhitespace.foreground': '#374151',
    'editorIndentGuide.background': '#374151',
    'editorIndentGuide.activeBackground': '#6b7280',
    'scrollbarSlider.background': '#374151',
    'scrollbarSlider.hoverBackground': '#4b5563',
    'scrollbarSlider.activeBackground': '#6b7280',
  },
};

const LIGHT_THEME = {
  base: 'vs',
  inherit: true,
  rules: [
    { token: '', foreground: '#12141a', background: '#ffffff' },
    { token: 'comment', foreground: '#6b7280' },
    { token: 'keyword', foreground: '#2563eb' },
    { token: 'string', foreground: '#059669' },
    { token: 'number', foreground: '#d97706' },
    { token: 'function', foreground: '#7c3aed' },
    { token: 'variable', foreground: '#12141a' },
    { token: 'type', foreground: '#0891b2' },
  ],
  colors: {
    'editor.background': '#f1f3f8',
    'editor.foreground': '#1a1d26',
    'editorLineNumber.foreground': '#8b919c',
    'editor.selectionBackground': '#0483b630',
    'editorCursor.foreground': '#1a1d26',
    'editor.lineHighlightBackground': '#e6e9f0',
    'editorGutter.background': '#f1f3f8',
    'editor.lineHighlightBorder': '#0483b615',
    'editorWhitespace.foreground': '#c5cad3',
    'editorIndentGuide.background': '#d8dce3',
    'editorIndentGuide.activeBackground': '#9aa1ad',
    'scrollbarSlider.background': '#c5cad3',
    'scrollbarSlider.hoverBackground': '#a8afbb',
    'scrollbarSlider.activeBackground': '#8b919c',
  },
};

function ToolbarIconCopy({ className }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function ToolbarIconFormat({ className }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
      <path d="M5 3v4M19 17v4M3 5h4M17 19h4" />
    </svg>
  );
}

function ToolbarIconExpand({ className }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
}

function ToolbarIconCheck({ className }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy copy */
    }
  }

  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * StudyModeCodeEditor — Monaco editor for study / creator flows.
 *
 * Optional `toolbarChrome` shows a VS Code–style bar (filename, language badge, actions).
 */
const StudyModeCodeEditor = ({
  value,
  onChange,
  language = 'javascript',
  placeholder = 'Enter your code here...',
  readOnly = false,
  onMount,
  autoFocus = false,
  toolbarChrome = false,
  toolbarFilename = 'main.js',
  showToolbarFilename = true,
  toolbarLanguageLabel,
  toolbarLanguageDot,
  expanded = false,
  onExpandClick,
  onIndexClick,
  indexOpen = false,
  indexTitle = 'Open snippet index',
  toolbarFilenameEditable = false,
  onToolbarFilenameChange,
  toolbarFilenamePlaceholder = 'Snippet name',
  onSaveClick,
  saveSaved = false,
  saveDisabled = false,
  saveTitle = 'Save snippet',
  alternateContent,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const surfaceRef = useRef(null);
  const copyResetTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const [bounds, setBounds] = useState({ width: 0, height: 320 });
  const [copied, setCopied] = useState(false);
  const { isLight } = useTheme();
  const themeName = isLight ? LIGHT_THEME_NAME : DARK_THEME_NAME;

  const langBadge = toolbarLanguageLabel || language;

  useLayoutEffect(() => {
    if (alternateContent) return undefined;

    const el = surfaceRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const applyLayout = () => {
      if (!mountedRef.current) return;
      const rect = el.getBoundingClientRect();
      const width = Math.max(0, Math.floor(rect.width));
      const height = Math.max(160, Math.floor(rect.height));
      setBounds((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height }
      );
      const ed = editorRef.current;
      if (ed && width > 0 && height > 0) {
        try {
          ed.layout({ width, height });
        } catch (_) {
          /* editor may be mid-dispose */
        }
      }
    };

    applyLayout();
    const ro = new ResizeObserver(applyLayout);
    ro.observe(el);
    return () => ro.disconnect();
  }, [toolbarChrome, expanded, alternateContent]);

  const handleBeforeMount = (monaco) => {
    monaco.editor.defineTheme(DARK_THEME_NAME, DARK_THEME);
    monaco.editor.defineTheme(LIGHT_THEME_NAME, LIGHT_THEME);
  };

  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(themeName);
    }
  }, [themeName]);

  const handleEditorDidMount = (editor, monaco) => {
    monacoRef.current = monaco;
    if (monaco) {
      monaco.editor.setTheme(themeName);
    }
    editorRef.current = editor;

    const layoutNow = () => {
      const surface = surfaceRef.current;
      if (!surface) return;
      const rect = surface.getBoundingClientRect();
      const width = Math.max(0, Math.floor(rect.width));
      const height = Math.max(160, Math.floor(rect.height));
      if (width > 0 && height > 0) {
        setBounds((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height }
        );
        editor.layout({ width, height });
      }
    };

    requestAnimationFrame(() => {
      layoutNow();
      requestAnimationFrame(layoutNow);
    });

    if (autoFocus) {
      setTimeout(() => {
        editor.focus();
      }, 100);
    }

    if (onMount) {
      onMount(editor, monaco);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      editorRef.current = null;
      monacoRef.current = null;
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const editor = editorRef.current;
    const text = editor ? editor.getValue() : (value ?? '');
    const didCopy = await copyTextToClipboard(text);
    if (!didCopy) return;

    setCopied(true);
    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current);
    }
    copyResetTimerRef.current = setTimeout(() => {
      setCopied(false);
      copyResetTimerRef.current = null;
    }, 400);
  }, [value]);

  const handleFormat = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.getAction('editor.action.formatDocument')?.run();
  }, []);

  const normalizeLanguageForMonaco = (lang) => {
    if (!lang) return 'javascript';
    const l = String(lang).toLowerCase();

    if (l.includes('c++') || l === 'cpp') return 'cpp';
    if (l.includes('c#') || l === 'csharp') return 'csharp';
    if (l === 'c') return 'c';
    if (l === 'js' || l.includes('javascript')) return 'javascript';
    if (l === 'ts' || l.includes('typescript')) return 'typescript';
    if (l.includes('python') || l === 'py') return 'python';
    if (l.includes('java')) return 'java';
    if (l.includes('ruby')) return 'ruby';
    if (l === 'go' || l.includes('golang')) return 'go';
    if (l.includes('php')) return 'php';
    if (l.includes('rust')) return 'rust';
    if (l.includes('kotlin')) return 'kotlin';
    if (l.includes('swift')) return 'swift';

    return l;
  };

  const monacoLanguage = normalizeLanguageForMonaco(language);

  const dotStyle = toolbarLanguageDot
    ? { '--study-mode-code-editor-toolbar-dot': toolbarLanguageDot }
    : undefined;

  return (
    <div
      className={[
        'study-mode-code-editor-container',
        toolbarChrome ? 'study-mode-code-editor-container--with-toolbar' : '',
        toolbarChrome && !showToolbarFilename
          ? 'study-mode-code-editor-container--toolbar-no-filename'
          : '',
        expanded ? 'study-mode-code-editor-container--expanded' : '',
      ].filter(Boolean).join(' ')}
    >
      {toolbarChrome && (
        <div className="study-mode-code-editor-toolbar">
          {typeof onIndexClick === 'function' && (
            <div className="study-mode-code-editor-toolbar-start">
              <button
                type="button"
                className={[
                  'study-mode-code-editor-toolbar-action',
                  'study-mode-code-editor-toolbar-action--index',
                  indexOpen && 'study-mode-code-editor-toolbar-action--active',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={(event) => {
                  event.stopPropagation();
                  onIndexClick(event);
                }}
                aria-label={indexOpen ? 'Close snippet index' : indexTitle}
                aria-pressed={indexOpen}
                title={indexOpen ? 'Close snippet index' : indexTitle}
              >
                <FaList className="study-mode-code-editor-toolbar-action-icon" aria-hidden />
              </button>
            </div>
          )}
          {showToolbarFilename && (
            toolbarFilenameEditable ? (
              <input
                type="text"
                className="study-mode-code-editor-toolbar-filename-input"
                value={toolbarFilename}
                onChange={(event) => onToolbarFilenameChange?.(event.target.value)}
                placeholder={toolbarFilenamePlaceholder}
                aria-label="Snippet name"
                title={toolbarFilename || toolbarFilenamePlaceholder}
              />
            ) : (
              <span className="study-mode-code-editor-toolbar-filename" title={toolbarFilename}>
                {toolbarFilename}
              </span>
            )
          )}
          <div className="study-mode-code-editor-toolbar-end">
            <span
              className="study-mode-code-editor-toolbar-lang-badge"
              style={dotStyle}
            >
              <span className="study-mode-code-editor-toolbar-lang-dot" aria-hidden />
              <span className="study-mode-code-editor-toolbar-lang-text">{langBadge}</span>
            </span>
            <div className="study-mode-code-editor-toolbar-actions">
              {typeof onSaveClick === 'function' && (
                <button
                  type="button"
                  className={[
                    'study-mode-code-editor-toolbar-action',
                    'study-mode-code-editor-toolbar-action--save',
                    saveSaved && 'study-mode-code-editor-toolbar-action--saved',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={onSaveClick}
                  disabled={saveDisabled || saveSaved}
                  aria-label={saveSaved ? 'Snippet saved' : saveTitle}
                  title={saveSaved ? 'Saved' : saveTitle}
                >
                  {saveSaved ? (
                    <ToolbarIconCheck className="study-mode-code-editor-toolbar-action-icon" />
                  ) : (
                    <FaSave className="study-mode-code-editor-toolbar-action-icon" aria-hidden />
                  )}
                </button>
              )}
              <button
                type="button"
                className={[
                  'study-mode-code-editor-toolbar-action',
                  copied && 'study-mode-code-editor-toolbar-action--copied',
                ].filter(Boolean).join(' ')}
                onClick={handleCopy}
                aria-label={copied ? 'Copied to clipboard' : 'Copy code'}
                title={copied ? 'Copied!' : 'Copy'}
              >
                {copied ? (
                  <ToolbarIconCheck className="study-mode-code-editor-toolbar-action-icon" />
                ) : (
                  <ToolbarIconCopy className="study-mode-code-editor-toolbar-action-icon" />
                )}
              </button>
              <button
                type="button"
                className="study-mode-code-editor-toolbar-action"
                onClick={handleFormat}
                aria-label="Format document"
                title="Format (Shift+Alt+F)"
              >
                <ToolbarIconFormat className="study-mode-code-editor-toolbar-action-icon" />
              </button>
              {typeof onExpandClick === 'function' && (
                <button
                  type="button"
                  className={[
                    'study-mode-code-editor-toolbar-action',
                    expanded ? 'study-mode-code-editor-toolbar-action--active' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpandClick(e);
                  }}
                  aria-label={expanded ? 'Shrink editor' : 'Expand editor'}
                  aria-pressed={expanded}
                  title={expanded ? 'Shrink' : 'Expand'}
                >
                  <ToolbarIconExpand className="study-mode-code-editor-toolbar-action-icon" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {alternateContent ? (
        <div className="study-mode-code-editor-alternate">{alternateContent}</div>
      ) : (
        <>
          <div ref={surfaceRef} className="study-mode-code-editor-surface">
            <MonacoEditor
              value={value}
              onChange={onChange}
              language={monacoLanguage}
              width={bounds.width > 0 ? bounds.width : '100%'}
              height={bounds.height > 0 ? bounds.height : 320}
              theme={themeName}
              beforeMount={handleBeforeMount}
              options={{
                fontFamily: "'JetBrains Mono', 'Fira Mono', 'Menlo', monospace",
                fontSize: 14,
                lineHeight: 20,
                lineNumbers: 'on',
                minimap: { enabled: false },
                smoothScrolling: true,
                scrollbar: {
                  vertical: 'auto',
                  horizontal: 'auto',
                  verticalScrollbarSize: 8,
                  horizontalScrollbarSize: 8
                },
                roundedSelection: true,
                renderLineHighlight: 'line',
                overviewRulerLanes: 0,
                renderIndentGuides: true,
                folding: false,
                contextmenu: false,
                renderLineHighlightOnlyWhenFocus: true,
                readOnly,
                padding: { top: 8, bottom: 16, left: 16, right: 16 },
                wordWrap: 'on',
                automaticLayout: false,
                scrollBeyondLastLine: false,
                tabSize: 2,
                glyphMargin: false,
              }}
              onMount={handleEditorDidMount}
            />
            {!value && (
              <div className="study-mode-code-editor-placeholder">
                {placeholder}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StudyModeCodeEditor;
